import React from "react";
import { X } from "lucide-react";
import Input from "./Input";
import { parseRankInput, rankIsUnusable } from "../../utils/helpers";

/**
 * The billing position of one artist on one event: a whole number >= 1, or empty for unranked.
 *
 * Empty is a real answer — a billing order read off a poster is often unknown, and a guessed rank
 * says something the source does not. The clear button exists because a box pre-filled with
 * someone else's guess gives no sign that emptying it is allowed; without it an admin either keeps
 * a rank they cannot vouch for or selects the text and deletes it, which is the same action with
 * no affordance.
 *
 * Value stays a string (the raw box contents), not a parsed rank: a half-typed "1" and a cleared
 * box are different states, and `rankIsUnusable` is how a caller refuses to save the middle ones.
 */
export function BillingRankInput({ value, onChange, name, disabled = false }) {
  const shown = value ?? "";
  return (
    <span className="flex items-center gap-1">
      <label className="flex items-center gap-1">
        Billing
        <Input
          type="number"
          min="1"
          step="1"
          className="h-7 w-16 px-2 py-1 text-xs"
          placeholder="—"
          title={`Billing position for ${name} (1 = headliner); leave empty for unranked`}
          value={shown}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
        />
      </label>
      {String(shown) !== "" && !disabled && (
        <button
          type="button"
          title={`Clear the billing position for ${name} — we do not know it`}
          aria-label={`Clear the billing position for ${name}`}
          className="hover:text-foreground"
          onClick={() => onChange("")}
        >
          <X className="h-3 w-3" />
        </button>
      )}
      {parseRankInput(shown) === 1 ? <span>headliner</span> : null}
      {rankIsUnusable(shown) ? <span className="text-destructive">whole number, 1 or more</span> : null}
    </span>
  );
}

export default BillingRankInput;
